import "@/App.css";
import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import { WebSocketProvider } from "./contexts/WebSocketContext";
import { Toaster } from "./components/ui/sonner";
import Footer from "./components/Footer";
import ChatNotificationPopup from "./components/ChatNotificationPopup";
import KeyboardShortcuts from "./components/KeyboardShortcuts";
import WebSocketNotifier from "./components/WebSocketNotifier";
import EmailVerificationModal from "./components/EmailVerificationModal";
import useEmailVerificationGuard from "./hooks/useEmailVerificationGuard";
import { Loader2 } from "lucide-react";

// Critical Pages (eager load - auth flow and main hub)
import Welcome from "./pages/Welcome";
import Login from "./pages/Login";
import Register from "./pages/Register";
import PlannerHub from "./pages/PlannerHub";
import CalendarView from "./pages/CalendarView";
import TodoLists from "./pages/TodoLists";
import EventDetail from "./pages/EventDetail";
import Inbox from "./pages/Inbox";
import MyHolds from "./pages/MyHolds";
import PrivateEvents from "./pages/PrivateEvents";

// Lazy-loaded Pages (less frequently accessed)
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Conversation = lazy(() => import("./pages/Conversation"));
const Account = lazy(() => import("./pages/Account"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminAbuseDashboard = lazy(() => import("./pages/AdminAbuseDashboard"));
const MarketInsights = lazy(() => import("./pages/MarketInsights"));
const MyStats = lazy(() => import("./pages/MyStats"));
const PlannerDirectory = lazy(() => import("./pages/PlannerDirectory"));
const Templates = lazy(() => import("./pages/Templates"));
const CreatePage = lazy(() => import("./pages/CreatePage"));
const SharedLists = lazy(() => import("./pages/SharedLists"));
const GroupsList = lazy(() => import("./pages/Groups").then(m => ({ default: m.GroupsList })));
const GroupDetail = lazy(() => import("./pages/Groups").then(m => ({ default: m.GroupDetail })));
const PlannerCirclesList = lazy(() => import("./pages/PlannerCircles").then(m => ({ default: m.PlannerCirclesList })));
const PlannerCircleDetail = lazy(() => import("./pages/PlannerCircles").then(m => ({ default: m.PlannerCircleDetail })));
const TodaySummary = lazy(() => import("./pages/TodaySummary"));
const WeeklyPlanning = lazy(() => import("./pages/WeeklyPlanning"));
const DirectMessages = lazy(() => import("./pages/DirectMessages"));
const DeepSearch = lazy(() => import("./pages/DeepSearch"));
const Credits = lazy(() => import("./pages/Credits"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));

// Components
import BottomNav from "./components/BottomNav";

// Loading Fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

// Protected Route Component - Auth required, guests redirected to hub
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, isGuestMode } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  // Guests are redirected to hub
  if (isGuestMode) {
    return <Navigate to="/hub" replace />;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Guest-allowed Route Component (for routes guests CAN access: Hub, Event Detail)
const GuestAllowedRoute = ({ children }) => {
  const { isAuthenticated, loading, isGuestMode } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  // Allow access if authenticated OR in guest mode
  if (!isAuthenticated && !isGuestMode) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Auth-only Route Component (guests NOT allowed - e.g., Settings, Credits)
const AuthOnlyRoute = ({ children }) => {
  const { isAuthenticated, loading, isGuestMode } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  // Guests are redirected to hub
  if (isGuestMode) {
    return <Navigate to="/hub" replace />;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public Routes - No auth required */}
        <Route path="/" element={<Welcome />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        
        {/* Guest-Allowed Routes - Auth OR Guest mode */}
        <Route path="/hub" element={
          <GuestAllowedRoute>
            <PlannerHub />
          </GuestAllowedRoute>
        } />
        <Route path="/events/:id" element={
          <GuestAllowedRoute>
            <EventDetail />
          </GuestAllowedRoute>
        } />
        
        {/* Auth-Only Routes - NO guest access */}
        <Route path="/account" element={
          <GuestAllowedRoute>
            <Account />
          </GuestAllowedRoute>
        } />
        <Route path="/credits" element={
          <AuthOnlyRoute>
            <Credits />
          </AuthOnlyRoute>
        } />
        <Route path="/deep-search" element={
          <ProtectedRoute>
            <DeepSearch />
          </ProtectedRoute>
        } />
        <Route path="/admin" element={
          <AuthOnlyRoute>
            <AdminDashboard />
          </AuthOnlyRoute>
        } />
        <Route path="/admin/abuse" element={
          <AuthOnlyRoute>
            <AdminAbuseDashboard />
          </AuthOnlyRoute>
        } />
        
        {/* Protected Routes - Auth required (guests redirected) */}
        <Route path="/my-holds" element={
          <ProtectedRoute>
            <MyHolds />
          </ProtectedRoute>
        } />
        <Route path="/private-events" element={
          <ProtectedRoute>
            <PrivateEvents />
          </ProtectedRoute>
        } />
        <Route path="/conversations/:id" element={
          <ProtectedRoute>
            <Conversation />
          </ProtectedRoute>
        } />
      <Route path="/calendar" element={
        <GuestAllowedRoute>
          <CalendarView />
        </GuestAllowedRoute>
      } />
      <Route path="/insights" element={
        <ProtectedRoute>
          <MarketInsights />
        </ProtectedRoute>
      } />
      <Route path="/my-stats" element={
        <ProtectedRoute>
          <MyStats />
        </ProtectedRoute>
      } />
      <Route path="/directory" element={
        <GuestAllowedRoute>
          <PlannerDirectory />
        </GuestAllowedRoute>
      } />
      <Route path="/create" element={
        <GuestAllowedRoute>
          <CreatePage />
        </GuestAllowedRoute>
      } />
      <Route path="/direct-messages/:id" element={
        <ProtectedRoute>
          <DirectMessages />
        </ProtectedRoute>
      } />
      <Route path="/templates" element={
        <ProtectedRoute>
          <Templates />
        </ProtectedRoute>
      } />
      {/* Redirect old my-events route to calendar */}
      <Route path="/my-events" element={<Navigate to="/calendar" replace />} />
      <Route path="/get-it-done" element={
        <GuestAllowedRoute>
          <TodoLists />
        </GuestAllowedRoute>
      } />
      <Route path="/shared-lists" element={
        <ProtectedRoute>
          <SharedLists />
        </ProtectedRoute>
      } />
      
      {/* Groups */}
      <Route path="/groups" element={
        <ProtectedRoute>
          <GroupsList />
        </ProtectedRoute>
      } />
      <Route path="/groups/:groupId" element={
        <ProtectedRoute>
          <GroupDetail />
        </ProtectedRoute>
      } />
      
      {/* Planner Circles */}
      <Route path="/planner-circles" element={
        <ProtectedRoute>
          <PlannerCirclesList />
        </ProtectedRoute>
      } />
      <Route path="/planner-circles/:circleId" element={
        <ProtectedRoute>
          <PlannerCircleDetail />
        </ProtectedRoute>
      } />
      
      {/* Inbox & Notifications */}
      <Route path="/inbox" element={
        <GuestAllowedRoute>
          <Inbox />
        </GuestAllowedRoute>
      } />
      <Route path="/notifications" element={<Navigate to="/directory" replace />} />
      <Route path="/today" element={
        <ProtectedRoute>
          <TodaySummary />
        </ProtectedRoute>
      } />
      <Route path="/week" element={
        <ProtectedRoute>
          <WeeklyPlanning />
        </ProtectedRoute>
      } />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

import QuickActionsFAB from "./components/QuickActionsFAB";

// Conditional Bottom Nav - shows for authenticated users and guest mode
const ConditionalBottomNav = () => {
  const { isAuthenticated, isGuestMode } = useAuth();
  if (!isAuthenticated && !isGuestMode) return null;
  return <BottomNav />;
};

// Global email verification guard
const EmailVerificationGuard = () => {
  const { showModal, onVerified, onClose } = useEmailVerificationGuard();
  return <EmailVerificationModal isOpen={showModal} onClose={onClose} onVerified={onVerified} />;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <NotificationProvider>
          <WebSocketProvider>
            <div className="App flex flex-col min-h-screen w-full max-w-full overflow-x-hidden overflow-y-visible">
              <BrowserRouter>
                <div 
                  className="flex-1 min-h-0 pb-4 w-full overflow-x-hidden overflow-y-auto"
                  style={{ WebkitOverflowScrolling: 'touch' }}
                >
                  <AppRoutes />
                </div>
                <Footer />
                <ConditionalBottomNav />
                <QuickActionsFAB />
                <ChatNotificationPopup />
                <KeyboardShortcuts />
                <WebSocketNotifier />
              </BrowserRouter>
              <EmailVerificationGuard />
              <Toaster position="top-right" richColors />
            </div>
          </WebSocketProvider>
        </NotificationProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
